import { Tabs, TabsContent, TabsList, TabsTrigger } from '@radix-ui/react-tabs'
import clsx from 'clsx'
import { useState } from 'react'

import { Slider } from 'components/ui/slider'
import { Button } from 'slab'

import { emitEmployerOnboardingEvent } from '../../utils'
import { DEPENDENT_COVERAGE_PERCENTAGES } from '../consts'
import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'

interface SmartDependentCoverageStepProps {
  onBack: () => void
}

export function SmartDependentCoverageStep ({ onBack }: SmartDependentCoverageStepProps) {
  const { filterValues, setFilterValues } = useSelfServeExplorerContext()
  const [activeTab, setActiveTab] = useState('simple')

  // For simple mode, both spouse and child get the same percentage
  const currentSimplePercentage = Math.round(filterValues.employerSpouseContribution * 100)

  const handleSimplePercentageChange = (percentage: number) => {
    const value = percentage / 100
    setFilterValues({
      ...filterValues,
      employerSpouseContribution: value,
      employerChildContribution: value,
    })
    emitEmployerOnboardingEvent('[ER] AllowanceTool dependent coverage selected', {})
  }

  return (
    <div className='flex flex-col gap-4 p-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='heading-sm text-gray-800'>Dependent coverage</h2>
          <p className='body text-gray-500 mt-1'>
            Define the coverage amounts for your employees' dependents.
          </p>
        </div>
        <Button variant='secondary' size='small' onClick={onBack}>
          Back
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className='flex flex-row gap-4 border-b border-gray-200'>
          <TabsTrigger
            value='simple'
            className='text-gray-800 body data-[state=active]:body-bold data-[state=active]:border-b-2 border-gray-800 p-2'
          >
            Simple
          </TabsTrigger>
          <TabsTrigger
            value='advanced'
            className='text-gray-800 body data-[state=active]:body-bold data-[state=active]:border-b-2 border-gray-800 p-2'
          >
            Advanced
          </TabsTrigger>
        </TabsList>

        <TabsContent value='simple' className='pt-6'>
          <div className='flex rounded-2xl border border-gray-200 bg-white p-1'>
            {DEPENDENT_COVERAGE_PERCENTAGES.map((percentage) => {
              const isSelected = currentSimplePercentage === percentage
              return (
                <button
                  key={percentage}
                  type='button'
                  className={clsx(
                    'flex-1 text-center',
                    'flex items-center justify-center px-4 py-2',
                    'body rounded-xl border',
                    'transition-colors duration-200',
                    isSelected
                      ? 'bg-thatch-100 border-thatch-500 text-gray-800 font-semibold'
                      : 'border-transparent bg-transparent text-gray-700 hover:bg-gray-50'
                  )}
                  onClick={() => handleSimplePercentageChange(percentage)}
                >
                  {percentage}%
                </button>
              )
            })}
          </div>
        </TabsContent>

        <TabsContent value='advanced' className='pt-6'>
          <div className='flex flex-col gap-6'>
            <div className='flex items-center gap-4'>
              <span className='body text-gray-700 w-48'>First dependent</span>
              <span className='body-bold text-gray-800 w-12'>
                {Math.round(filterValues.employerSpouseContribution * 100)}%
              </span>
              <div className='flex-1'>
                <Slider
                  min={0}
                  max={100}
                  value={[Math.round(filterValues.employerSpouseContribution * 100)]}
                  trackColor='bg-gray-300'
                  rangeColor='bg-gray-800'
                  onValueChange={value => {
                    const newValue = value[0] / 100
                    setFilterValues({ ...filterValues, employerSpouseContribution: newValue })
                    emitEmployerOnboardingEvent('[ER] AllowanceTool custom dependent 1 coverage updated', {})
                  }}
                />
              </div>
            </div>

            <div className='flex items-center gap-4'>
              <span className='body text-gray-700 w-48'>Additional dependents</span>
              <span className='body-bold text-gray-800 w-12'>
                {Math.round(filterValues.employerChildContribution * 100)}%
              </span>
              <div className='flex-1'>
                <Slider
                  min={0}
                  max={100}
                  value={[Math.round(filterValues.employerChildContribution * 100)]}
                  trackColor='bg-gray-300'
                  rangeColor='bg-gray-800'
                  onValueChange={value => {
                    const newValue = value[0] / 100
                    setFilterValues({ ...filterValues, employerChildContribution: newValue })
                    emitEmployerOnboardingEvent('[ER] AllowanceTool custom dependent addtl coverage updated', {})
                  }}
                />
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
